import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { vendorApi } from '../../services/vendor';
import type { VendorConnection } from '../../types';
import extractApiError from '../../utils/extractApiError';

const statusVariant = (s: VendorConnection['status']) =>
  s === 'active' ? 'success' : s === 'pending' ? 'warning' : 'error';

type Tab = 'all' | 'pending' | 'active' | 'declined';

export default function VendorSiteInvitations() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('all');

  const { data: connectionsData, isLoading } = useQuery({
    queryKey: ['vendor', 'connections'],
    queryFn: () => vendorApi.getConnections().then((r) => r.data),
  });

  const { data: stats } = useQuery({
    queryKey: ['vendor', 'invitation-stats'],
    queryFn: () => vendorApi.getInvitationStats().then((r) => r.data),
  });

  const respondMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'reject' }) =>
      vendorApi.respondToConnection(id, action),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vendor', 'connections'] });
      queryClient.invalidateQueries({ queryKey: ['vendor', 'invitation-stats'] });
      setToast(variables.action === 'accept' ? 'Connection accepted!' : 'Connection declined.');
      setTimeout(() => setToast(null), 3000);
    },
    onError: (err: unknown) => {
      setToast(extractApiError(err));
      setTimeout(() => setToast(null), 4000);
    },
  });

  const connections: VendorConnection[] = connectionsData?.results ?? [];
  const filtered = tab === 'all'
    ? connections
    : connections.filter((c) => {
      if (tab === 'declined') return c.status === 'rejected';
      return c.status === tab;
    });

  return (
    <>
      <PageHeader
        title="Site Invitations"
        description="Manage connection requests from clients"
      />

      {/* Toast */}
      {toast && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-700">{toast}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-xs font-medium text-slate-400 uppercase">Pending</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{stats?.pending_count ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-xs font-medium text-slate-400 uppercase">Avg Response Time</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{stats?.avg_response_hours ?? 0}h</p>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-xs font-medium text-slate-400 uppercase">Acceptance Rate</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{stats?.acceptance_rate ?? 0}%</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        {(['all', 'pending', 'active', 'declined'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors capitalize ${
              tab === t ? 'bg-brand-100 text-brand-700' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No invitations"
          description={tab === 'all' ? 'Connection requests from clients will appear here.' : `No ${tab} invitations.`}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((conn) => (
            <div key={conn.id} className="rounded-2xl border border-slate-100 bg-white p-5 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">{conn.client_org.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Requested {new Date(conn.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge label={conn.status} variant={statusVariant(conn.status)} />
                {conn.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => respondMutation.mutate({ id: conn.id, action: 'accept' })}
                      disabled={respondMutation.isPending}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => respondMutation.mutate({ id: conn.id, action: 'reject' })}
                      disabled={respondMutation.isPending}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
